import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { twMergeConfig } from "@repo/utils/twMergeConfig";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { Link } from "./Link";

// Storyblok rich text node types
interface RichTextNode {
  type: string;
  content?: RichTextNode[];
  attrs?: Record<string, unknown>;
  marks?: Mark[];
  text?: string;
}

interface Mark {
  type: string;
  attrs?: Record<string, unknown>;
}

// Component props
export interface AutomatedArticleRichtextProps extends ComponentProps<"div"> {
  richtext: {
    content: RichTextNode[];
  };
  className?: string;
}

// Styling variants for different elements
const headingStyles = tv(
  {
    variants: {
      level: {
        1: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-1 uipm:rich-aut-heading",
          "uipm:text-[var(--typography-colors-text-heading)]",
        ),
        2: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-5 uipm:rich-aut-heading",
          "uipm:text-[var(--typography-colors-text-heading)]",
        ),
        3: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-5 uipm:rich-aut-heading",
          "uipm:text-[var(--typography-colors-text-heading)]",
        ),
        4: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-5 uipm:rich-aut-heading",
          "uipm:text-[var(--typography-colors-text-heading)]",
        ),
        5: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-5 uipm:rich-aut-heading",
          "uipm:text-[var(--typography-colors-text-heading)]",
        ),
        6: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-5 uipm:rich-aut-heading",
          "uipm:text-[var(--typography-colors-text-heading)]",
        ),
      },
    },
  },
  {
    twMergeConfig,
  },
);

const paragraphStyles = tv(
  {
    base: cn(
      "uipm:font-[var(--font-family-sans)]",
      "uipm:font-medium",
      "uipm:fz-[var(--typography-sizes-body-medium)]",
      "uipm:leading-[var(--typography-line-heights-body-medium)]",
      "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "uipm:mb-[var(--typography-line-heights-body-medium)]",
    ),
    variants: {
      isHeading: {
        true: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:heading-5",
          "uipm:text-[var(--typography-colors-text-heading)]",
          "uipm:mb-[var(--container-title-gap)]",
        ),
      },
    },
  },
  {
    twMergeConfig,
  },
);

const listStyles = tv(
  {
    variants: {
      type: {
        ul: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:font-medium",
          "uipm:list-disc uipm:list-outside uipm:ml-6",
          "uipm:fz-[var(--typography-sizes-body-medium)]",
          "uipm:leading-[var(--typography-line-heights-body-medium)]",
          "uipm:text-[var(--typography-colors-text-faded-dark)]",
        ),
        ol: cn(
          "uipm:font-[var(--font-family-sans)]",
          "uipm:font-medium",
          "uipm:list-decimal uipm:list-outside uipm:ml-6",
          "uipm:fz-[var(--typography-sizes-body-medium)]",
          "uipm:leading-[var(--typography-line-heights-body-medium)]",
          "uipm:text-[var(--typography-colors-text-faded-dark)]",
        ),
      },
    },
  },
  {
    twMergeConfig,
  },
);

const listItemStyles = tv(
  {
    base: cn(
      "uipm:font-[var(--font-family-sans)]",
      "uipm:font-medium",
      "uipm:mb-2",
      "uipm:fz-[var(--typography-sizes-body-medium)]",
      "uipm:leading-[var(--typography-line-heights-body-medium)]",
      "uipm:text-[var(--typography-colors-text-faded-dark)]",
    ),
  },
  {
    twMergeConfig,
  },
);

const imageWrapper = tv({
  base: cn(
    "uipm:relative uipm:w-full uipm:my-4 uipm:min-h-[200px]",
    "uipm:overflow-hidden uipm:rounded-[var(--radius-radius-sm)]",
    "uipm:bg-gray-100",
  ),
});

const tableWrapperStyles = tv(
  {
    base: cn(
      "uipm:w-full uipm:overflow-x-auto uipm:my-6",
      "uipm:rounded-[var(--radius-radius-sm)]",
      "uipm:shadow-sm",
    ),
  },
  {
    twMergeConfig,
  },
);

const tableStyles = tv(
  {
    base: cn(
      "uipm:font-[var(--font-family-sans)]",
      "uipm:font-medium",
      "uipm:w-full uipm:min-w-[600px] uipm:border-collapse",
      "uipm:overflow-hidden",
    ),
  },
  {
    twMergeConfig,
  },
);

const theadStyles = tv(
  {
    base: cn("uipm:bg-[var(--surface-surface-accent)]"),
  },
  {
    twMergeConfig,
  },
);

const thStyles = tv(
  {
    base: cn(
      "uipm:font-[var(--font-family-sans)]",
      "uipm:font-medium",
      "uipm:px-6 uipm:py-4",
      "uipm:text-left uipm:font-bold",
      "uipm:fz-[var(--typography-sizes-body-large)]",
      "uipm:leading-[var(--typography-line-heights-body-large)]",
      "uipm:text-[var(--typography-colors-text-inverted)]",
      "uipm:border-b uipm:border-[var(--color-grey-300)]",
      "uipm:min-w-[150px]",
      "uipm:whitespace-normal",
    ),
  },
  {
    twMergeConfig,
  },
);

const tbodyStyles = tv(
  {
    base: cn(""),
  },
  {
    twMergeConfig,
  },
);

const trStyles = tv(
  {
    variants: {
      isEven: {
        true: cn("uipm:bg-[var(--color-grey-50)]"),
        false: cn("uipm:bg-[var(--surface-surface-primary)]"),
      },
      inThead: {
        true: cn("uipm:border-b uipm:border-[var(--color-grey-300)]"),
        false: cn(
          "uipm:border-b uipm:border-[var(--color-grey-300)]",
          "last:uipm:border-b-0",
          "hover:uipm:bg-[var(--surface-surface-accent-light)]",
        ),
      },
    },
  },
  {
    twMergeConfig,
  },
);

const tdStyles = tv(
  {
    base: cn(
      "uipm:font-[var(--font-family-sans)]",
      "uipm:font-medium",
      "uipm:px-6 uipm:py-4",
      "uipm:fz-[var(--typography-sizes-body-large)]",
      "uipm:leading-[var(--typography-line-heights-body-large)]",
      "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "uipm:min-w-[150px]",
      "uipm:whitespace-normal",
    ),
  },
  {
    twMergeConfig,
  },
);

// Helper function to apply text marks (bold, italic, etc.)
const applyMarks = (text: string, marks?: Mark[]): React.ReactNode => {
  if (!marks || marks.length === 0) {
    return text;
  }

  let result: React.ReactNode = text;

  // Check if there's a link mark to determine bold font weight
  const hasLink = marks.some((mark) => mark.type === "link");

  marks.forEach((mark) => {
    switch (mark.type) {
      case "bold": {
        // Check if THIS specific text is a special heading
        const isThisTextSpecialHeading =
          typeof text === "string" && isSpecialHeading(text);

        // Build class list for strong tag - bold text in blocks should look like headings
        const strongClasses = [
          "uipm:font-[var(--font-family-sans)]",
          "uipm:font-medium",
          "uipm:block",
          "uipm:heading-5",
          "uipm:text-[var(--typography-colors-text-heading)]",
          "uipm:mb-4",
        ];

        // Add special heading margins if this text matches our patterns
        if (isThisTextSpecialHeading) {
          strongClasses.push("uipm:mt-8");
        }

        result = <strong className={cn(...strongClasses)}>{result}</strong>;
        break;
      }
      case "italic":
        result = <em className="uipm:font-medium uipm:italic">{result}</em>;
        break;
      case "underline":
        result = <u className="uipm:font-medium uipm:underline">{result}</u>;
        break;
      case "strike":
        result = (
          <del className="uipm:font-medium uipm:line-through">{result}</del>
        );
        break;
      case "code":
        result = (
          <code className="uipm:font-mono uipm:bg-gray-100 uipm:px-1 uipm:rounded uipm:text-sm">
            {result}
          </code>
        );
        break;
      case "link":
        result = (
          <Link
            href={mark.attrs?.href as string}
            target={mark.attrs?.target as string}
            noFollow={
              (mark.attrs?.custom as { nofollow?: string })?.nofollow ===
                "true" || (mark.attrs?.noFollow as boolean)
            }
            className={cn(
              "uipm:font-[var(--font-family-sans)]",
              "uipm:text-[var(--typography-colors-link,#499B45)]",
              "uipm:font-medium",
              "uipm:fz-[var(--typography-sizes-body-medium,16px)]",
              "uipm:leading-[var(--typography-line-heights-body-medium,26px)]",
              "uipm:underline",
              "uipm:decoration-solid",
              "uipm:decoration-auto",
              "uipm:underline-offset-auto",
            )}
          >
            {result}
          </Link>
        );
        break;
    }
  });

  return result;
};

// Helper function to check if text matches special heading patterns
const isSpecialHeading = (text: string): boolean => {
  const specialHeadingPatterns = [
    /^About ParkMobile$/i,
    /^Press Contact ParkMobile:?$/i, // Optional colon
  ];

  const trimmedText = text.trim();
  return specialHeadingPatterns.some((pattern) => pattern.test(trimmedText));
};

// Main rendering function for rich text nodes
const renderRichTextNode = (
  node: RichTextNode,
  index: number,
): React.ReactNode => {
  switch (node.type) {
    case "doc":
      return (
        <div key={index} className="uipm:space-y-4">
          {node.content?.map((child, childIndex) =>
            renderRichTextNode(child, childIndex),
          )}
        </div>
      );

    case "paragraph": {
      // Check if paragraph contains only bold/strong text (treat as heading)
      const hasOnlyBoldText =
        node.content &&
        node.content.length === 1 &&
        node.content[0] &&
        node.content[0].type === "text" &&
        node.content[0].marks?.some((mark) => mark.type === "bold");

      return (
        <p
          key={index}
          className={paragraphStyles({ isHeading: hasOnlyBoldText })}
        >
          {node.content?.map((child, childIndex) => {
            if (child.type === "text" && child.text) {
              return applyMarks(child.text, child.marks);
            }
            return renderRichTextNode(child, childIndex);
          })}
        </p>
      );
    }

    case "heading": {
      const rawLevel = node.attrs?.level as number;
      const level = (
        rawLevel && rawLevel >= 1 && rawLevel <= 6 ? rawLevel : 3
      ) as 1 | 2 | 3 | 4 | 5 | 6;

      // Check if heading contains a link
      const hasLink = node.content?.some((child) =>
        child.marks?.some((mark) => mark.type === "link"),
      );

      const headingProps = {
        key: index,
        className: cn(
          headingStyles({ level }),
          hasLink &&
            cn(
              "uipm:text-[var(--typography-colors-link,#499B45)]",
              "uipm:font-medium",
              "uipm:fz-[var(--typography-sizes-body-medium,16px)]",
              "uipm:leading-[var(--typography-line-heights-body-medium,26px)]",
              "uipm:underline",
              "uipm:decoration-solid",
              "uipm:decoration-auto",
              "uipm:underline-offset-auto",
            ),
        ),
        children: node.content?.map((child, childIndex) => {
          if (child.type === "text" && child.text) {
            return applyMarks(child.text, child.marks);
          }
          return renderRichTextNode(child, childIndex);
        }),
      };

      switch (level) {
        case 1:
          return React.createElement("h1", headingProps);
        case 2:
          return React.createElement("h2", headingProps);
        case 3:
          return React.createElement("h3", headingProps);
        case 4:
          return React.createElement("h4", headingProps);
        case 5:
          return React.createElement("h5", headingProps);
        case 6:
          return React.createElement("h6", headingProps);
        default:
          return React.createElement("h3", headingProps);
      }
    }

    case "bullet_list":
      return (
        <ul key={index} className={listStyles({ type: "ul" })}>
          {node.content?.map((child, childIndex) =>
            renderRichTextNode(child, childIndex),
          )}
        </ul>
      );

    case "ordered_list":
      return (
        <ol key={index} className={listStyles({ type: "ol" })}>
          {node.content?.map((child, childIndex) =>
            renderRichTextNode(child, childIndex),
          )}
        </ol>
      );

    case "list_item":
      return (
        <li key={index} className={listItemStyles()}>
          {node.content?.map((child, childIndex) => {
            // For list items, we want to render the content without the extra paragraph wrapper
            if (child.type === "paragraph") {
              return child.content?.map((grandChild, grandChildIndex) => {
                if (grandChild.type === "text" && grandChild.text) {
                  return applyMarks(grandChild.text, grandChild.marks);
                }
                return renderRichTextNode(grandChild, grandChildIndex);
              });
            }
            return renderRichTextNode(child, childIndex);
          })}
        </li>
      );

    case "image": {
      const src = node.attrs?.src as string;
      const alt = (node.attrs?.alt as string) || "";
      const width = node.attrs?.width as number | undefined;
      const height = node.attrs?.height as number | undefined;

      if (!src) return null;

      // Calculate aspect ratio if dimensions are provided, otherwise use auto height
      const aspectRatio = width && height ? `${width}/${height}` : "16/9";

      return (
        <div key={index} className={imageWrapper()} style={{ aspectRatio }}>
          <Image
            src={src}
            alt={parseAiAlt(alt).cleanAlt}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
            className="uipm:object-contain"
            quality={80}
          />
          <AiDisclaimerForAlt alt={alt} src={src} />
        </div>
      );
    }

    case "hard_break":
      return <br key={index} />;

    case "text":
      if (node.text) {
        return applyMarks(node.text, node.marks);
      }
      return null;

    case "table":
      return (
        <div key={index} className={tableWrapperStyles()}>
          <table className={tableStyles()}>
            {node.content?.map((child, childIndex) =>
              renderRichTextNode(child, childIndex),
            )}
          </table>
        </div>
      );

    case "table_header":
      return (
        <thead key={index} className={theadStyles()}>
          {node.content?.map((child, childIndex) =>
            renderRichTextNode(child, childIndex),
          )}
        </thead>
      );

    case "table_body":
      return (
        <tbody key={index} className={tbodyStyles()}>
          {node.content?.map((child, childIndex) =>
            renderRichTextNode(child, childIndex),
          )}
        </tbody>
      );

    case "table_row": {
      // Check if this row is in thead or tbody
      const isEven = index % 2 === 0;
      const inThead = false; // We'll determine this from parent context

      return (
        <tr
          key={index}
          className={trStyles({ isEven: !inThead && isEven, inThead })}
        >
          {node.content?.map((child, childIndex) =>
            renderRichTextNode(child, childIndex),
          )}
        </tr>
      );
    }

    case "table_header_cell":
      return (
        <th key={index} className={thStyles()}>
          {node.content?.map((child, childIndex) => {
            if (child.type === "paragraph") {
              return child.content?.map((grandChild, grandChildIndex) => {
                if (grandChild.type === "text" && grandChild.text) {
                  return applyMarks(grandChild.text, grandChild.marks);
                }
                return renderRichTextNode(grandChild, grandChildIndex);
              });
            }
            if (child.type === "text" && child.text) {
              return applyMarks(child.text, child.marks);
            }
            return renderRichTextNode(child, childIndex);
          })}
        </th>
      );

    case "table_cell":
      return (
        <td key={index} className={tdStyles()}>
          {node.content?.map((child, childIndex) => {
            if (child.type === "paragraph") {
              return child.content?.map((grandChild, grandChildIndex) => {
                if (grandChild.type === "text" && grandChild.text) {
                  return applyMarks(grandChild.text, grandChild.marks);
                }
                return renderRichTextNode(grandChild, grandChildIndex);
              });
            }
            if (child.type === "text" && child.text) {
              return applyMarks(child.text, child.marks);
            }
            return renderRichTextNode(child, childIndex);
          })}
        </td>
      );

    default:
      // For unknown node types, try to render children if they exist
      if (node.content) {
        return (
          <div key={index}>
            {node.content.map((child, childIndex) =>
              renderRichTextNode(child, childIndex),
            )}
          </div>
        );
      }
      return null;
  }
};

export const AutomatedArticleRichtext = React.forwardRef<
  HTMLDivElement,
  AutomatedArticleRichtextProps
>(({ richtext, className, ...props }, ref) => {
  if (!richtext) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        "uipm:space-y-4 uipm:pb-[var(--container-section-bottom)]",
        className,
      )}
      {...props}
    >
      {richtext.content.map((node, index) => renderRichTextNode(node, index))}
    </div>
  );
});

AutomatedArticleRichtext.displayName = "AutomatedArticleRichtext";

export type AutomatedArticleRichtextVariants = VariantProps<
  typeof headingStyles
>;
